import React, { useEffect, useState, useRef } from "react";
import AuthHeader from "../components/auth-header";
import { useVerifyUserOTPMutation } from "store/services/authAPI";
import { useLocation } from "react-router-dom";
import { extractErrorMessage } from "utils/strings";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { login } from "store/reducers/AuthReducer";
import { useResendOTPMutation } from "store/services/authAPI";

const VerifyOTP = () => {
  const validatingID = false;
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const location = useLocation();
  const dispatch = useDispatch();
  const [verifyUserOTP, { isLoading: verifyingOTP }] = useVerifyUserOTPMutation();
  const [resendOTP, { isLoading: resendingOTP }] = useResendOTPMutation();


  // Get token from URL query
  const queryParams = new URLSearchParams(location.search);
  const sessionToken = queryParams.get("token");

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleOTPChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }
    
    setOtp(newOtp);
    
    // Focus the next empty input or the last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleResend = async () => {
    if (canResend && sessionToken) {
      setCountdown(30);
      setCanResend(false);
      try {
        await resendOTP({ session_id: sessionToken }).unwrap();
        // Show styled label message
        Swal.fire({
          title: undefined,
          html: '<label class="mb-1" style="display:block;"><strong>Another email has been sent, please enter the code</strong></label>',
          icon: undefined,
          showConfirmButton: false,
          timer: 2500,
          customClass: {
            popup: 'swal2-no-title',
          },
        });
      } catch (error) {
        Swal.fire({
          title: "Error Occurred",
          text: extractErrorMessage(error) || "Failed to resend OTP.",
          iconHtml:
            '<i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>',
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Try Again",
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length === 6 && sessionToken) {
      try {
        const data = await verifyUserOTP({ session_id: sessionToken, otp: otpValue }).unwrap();
        dispatch(login(data));
        // if (data?.user_data?.account_type === "FELLOW") {
        //   navigate("/fellow/dashboard");
        // } else if (data?.user_data?.account_type === "PROVIDER") {
        //   navigate("/provider/dashboard");
        // }
      } catch (error) {
        const errorMessage = extractErrorMessage(error) || "OTP verification failed. Please try again.";
        Swal.fire({
          title: "Error Occurred",
          text: errorMessage,
          iconHtml:
            '<i class="fa fa-exclamation-triangle text-danger" aria-hidden="true"></i>',
          showCloseButton: true,
          customClass: {
            icon: "no-border",
          },
          confirmButtonText: "Try Again",
        });
      }
    }
  };

  return (
    <>
      

       <div
        className="login-main-page auth-page"
        style={{
          backgroundSize: "contain",
        }}
      >
        <AuthHeader />
        <div className="login-wrapper w-100 mt-4">
          <div className="container h-100">
            <div className={`row h-100 align-items-center justify-content-center ${validatingID ? "disabled" : ""}`}>
              <div className="col-xl-6 mt-3">
                <div className="card auth-card auth-card-min-height">
                  <div className="card-body p-0">
                    <div className="row m-0">
                      <div className="col-xl-12 col-md-12">
                        <div className="sign-in-your">
                          <div>
                            <h4 className="registration-heading">
                              Verify your Identity
                            </h4>
                            <p className="registration-title mt-2">
                              We just want to be sure its you
                            </p>
                          </div>
                          
                          <form onSubmit={handleSubmit}>
                            <div className="form mt-4">
                              <div className="mb-3">
                                <label className="mb-1">
                                  <strong>Enter OTP</strong>
                                </label>
                                
                                <div className="otp-inputs">
                                  {otp.map((digit, index) => (
                                    <input
                                      key={index}
                                      ref={el => inputRefs.current[index] = el}
                                      type="text"
                                      inputMode="numeric"
                                      maxLength="1"
                                      value={digit}
                                      onChange={(e) => handleOTPChange(index, e.target.value.replace(/\D/g, ''))}
                                      onKeyDown={(e) => handleKeyDown(index, e)}
                                      onPaste={handlePaste}
                                      className="otp-input"
                                      placeholder="*"
                                    />
                                  ))}
                                </div>

                                <div className="otp-resend text-left">
                                  {/* Show spinner above button only while countdown is active */}
                                  {countdown > 0 && (
                                    <svg className="otp-spinner mb-1" viewBox="0 0 24 24" width="24" height="24">
                                      <circle 
                                        style={{ opacity: 0.25 }}
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="4"
                                        fill="none"
                                      />
                                      <path 
                                        style={{ opacity: 0.75 }}
                                        fill="currentColor" 
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                  )}
                                  <span>
                                    {canResend ? (
                                      <button 
                                        type="button"
                                        onClick={handleResend}
                                        className="otp-resend-button"
                                        disabled={resendingOTP}
                                      >
                                        {resendingOTP ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Resending...
                                          </>
                                        ) : (
                                          "Request again"
                                        )}
                                      </button>
                                    ) : (
                                      `Request again in ${countdown}s`
                                    )}
                                  </span>
                                </div>
                              </div>

                              <div className="text-center">
                                <button
                                  type="submit"
                                  className="btn btn-primary btn-block"
                                  disabled={otp.join('').length !== 6 || verifyingOTP || !sessionToken}
                                >
                                  {verifyingOTP ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Verifying...
                                    </>
                                  ) : (
                                    "Go to Dashboard"
                                  )}
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyOTP;